/*
 * MCRO001-F0050 / T-MCRO-050 (3/3) — wire document-merge into the recovery flow.
 * Design authority: choreo MCRO001-D004 §9 (recovery-state integration),
 * proposal §§3.2/9.1/9.2.
 *
 * This module is the THIN, PURE bridge between the collaboration hook's recovery
 * paths and the generic 3-way merge primitive (slice A `merge3`) + annotation
 * lexer (slice A `serialize`) + picker registry (slice B). It holds NO React, NO
 * hook refs and NO I/O — everything is a deterministic function of its string
 * inputs, so the merge decision is mutation-provable exactly like `merge3` itself.
 *
 * The hook calls attemptRecoveryMerge(base, ours, theirs) at the resync
 * integration point (useChoreographyCollaboration.js resync()). The three inputs
 * are always the EXACT source text (collaborationProtocol.exactSource):
 *   base   = exactSource of the client-held fork point (canonicalRef at last sync),
 *            or null when there is no usable base (cold reload / rotation) — the
 *            merge3 2-way DEGRADE path (§3.4).
 *   ours   = exactSource of the client's optimistic document (canonical + pending),
 *   theirs = exactSource of the freshly resynced server document.
 */

import { merge3, joinLinesKeepEol } from '../../lib/text-merge/merge3';
import { serialize } from '../../lib/annotation/annotationLexer';
import { lineEditOperations, orderedLines } from './collaborationProtocol';

/**
 * @typedef {Object} RecoveryMergeResult
 * @property {boolean} clean       True iff there were no conflicts (silent-apply path).
 * @property {boolean} degraded    True iff this was the 2-way degrade (no usable base).
 * @property {string}  merged      The auto-merged plain text (no boxes) — used on the
 *                                  clean path: apply silently, no panel.
 * @property {string}  annotated   The merged text with each conflict region rendered as an
 *                                  inline `[!!conflict: …-!!]` box (slice A serialize) —
 *                                  used on the conflict path: the recovery panel parses this
 *                                  back with the slice-A lexer and renders each box via
 *                                  AnnotationPicker. Equal to `merged` when clean.
 * @property {import('../../lib/text-merge/merge3').ConflictNode[]} conflicts
 */

/**
 * Serialize one merge conflict region into the inline annotation-box source the
 * picker consumes. The box `kind` is 'conflict' (well-known, slice B registry ->
 * ConflictPicker); `ours`/`theirs`/`base` carry the exact side text. `id` is left
 * empty — the producer/picker mints a stable UUIDv7 when it first binds (slice B
 * annotationId.ensureNodeId), keeping this function pure (no rng/clock).
 *
 * @param {import('../../lib/text-merge/merge3').ConflictNode} conflict
 * @returns {string}
 */
export function serializeConflictBox(conflict) {
  return serialize({
    kind: 'conflict',
    id: '',
    ours: joinLinesKeepEol(conflict.ours || []),
    theirs: joinLinesKeepEol(conflict.theirs || []),
    ...(conflict.base && conflict.base.length ? { base: joinLinesKeepEol(conflict.base) } : {}),
  });
}

/**
 * Rebuild the merged document as text, substituting each conflict region (which
 * `merge3` emitted as ours-then-theirs verbatim in `merged`) with its inline box.
 * `merge3` places conflict regions ours-then-theirs and everything else is the
 * auto-merged run, so we re-walk the region list to reconstruct the stream with
 * boxes in the exact positions. Text between/around conflicts is the auto-merged
 * text — reconstructed from the same region ordering merge3 used.
 *
 * We rebuild from the ordered regions rather than string-replacing on `merged`,
 * because a conflict's ours/theirs text can legitimately recur elsewhere in the
 * document and a naive replace would corrupt an unrelated identical run.
 *
 * @param {import('../../lib/text-merge/merge3').Merge3Result} result
 * @returns {string}
 */
function annotate(result) {
  // Re-derive the region stream deterministically: rerun merge3 is not needed —
  // the result already carries `merged` (auto-merged, conflicts inlined as
  // ours+theirs) and the ordered `conflicts`. We walk `merged` line-preservingly
  // and swap each conflict's ours+theirs run for its box, in order. Because
  // merge3 emits conflicts ours-then-theirs contiguously and in document order,
  // consuming them left-to-right against the same concatenation is exact.
  if (result.clean) return result.merged;
  let out = '';
  let cursor = 0;
  const merged = result.merged;
  for (const conflict of result.conflicts) {
    const oursText = joinLinesKeepEol(conflict.ours || []);
    const theirsText = joinLinesKeepEol(conflict.theirs || []);
    const region = oursText + theirsText;
    const at = merged.indexOf(region, cursor);
    if (at === -1) {
      // Defensive: a region we cannot re-locate means we cannot safely annotate
      // in place; fall back to appending the box so the conflict is never lost.
      out = merged;
      out += (out.endsWith('\n') || out === '' ? '' : '\n') + serializeConflictBox(conflict);
      cursor = merged.length;
      continue;
    }
    out += merged.slice(cursor, at);
    out += serializeConflictBox(conflict);
    cursor = at + region.length;
  }
  out += merged.slice(cursor);
  return out;
}

/**
 * Attempt a 3-way recovery merge. This is the single decision point the hook
 * defers to before it would otherwise land in the `UNSENDABLE` panel.
 *
 * @param {string|null|undefined} base   exactSource of the client fork point, or
 *                                        null/undefined to force the 2-way degrade.
 * @param {string} ours                  exactSource of the optimistic client document.
 * @param {string} theirs                exactSource of the resynced server document.
 * @returns {RecoveryMergeResult}
 */
export function attemptRecoveryMerge(base, ours, theirs) {
  const result = merge3(base, ours, theirs);
  return {
    clean: result.clean,
    degraded: result.degraded,
    merged: result.merged,
    annotated: annotate(result),
    conflicts: result.conflicts,
  };
}

/**
 * Reconcile a document to a target source text with the MINIMUM set of line-level
 * MCRO-COLLAB/1 operations, reusing the proven per-line differ (lineEditOperations).
 * Recovery-only: used when the user finishes resolving conflicts in the merge panel
 * and the finalised text must be submitted through the ordinary operation queue —
 * it never touches the live OT transform path.
 *
 * Strategy (line-keyed, deterministic, PURE):
 *   - Split the target into lines (terminators stripped for keying; the document
 *     stores text + eol separately). Existing lines are edited in place by index
 *     via lineEditOperations (text.insert/text.delete). A shorter target deletes
 *     the surplus trailing lines (line.delete). A longer target grows by splitting
 *     the last line and filling the new line (line.split then text.insert), with a
 *     caller-provided id generator for the new line ids.
 *
 * This is intentionally simple and index-aligned rather than an LCS: the merged
 * text is already the reconciled document, so index alignment against the adopted
 * canonical is the common shape, and any residual drift is corrected by the server
 * on submit exactly like any other queued edit.
 *
 * @param {object} document           The current canonical document (adopted server head).
 * @param {string} targetText         The finalised merged source text to reach.
 * @param {() => string} mintLineId   UUIDv7 generator for any new line ids.
 * @returns {Array<object>}           Line-level operations (possibly empty).
 */
export function sourceToLineOperations(document, targetText, mintLineId) {
  const lines = orderedLines(document);
  const targetLines = String(targetText ?? '').split('\n');
  const operations = [];

  const commonCount = Math.min(lines.length, targetLines.length);
  for (let index = 0; index < commonCount; index += 1) {
    operations.push(...lineEditOperations(lines[index].sourceLineId, lines[index].text, targetLines[index]));
  }

  // Target has FEWER lines: delete the surplus trailing lines (last-first so the
  // indices stay valid as the server applies them in order). NOTE: the collaborative
  // document stores each line's terminator (`eol`) separately, and MCRO-COLLAB/1 has
  // no "set eol" primitive — so a shrink leaves the new last line's stored eol as it
  // was. The server reconciles the final terminator on submit (the same
  // server-authoritative reconciliation every queued edit gets); locally the line
  // COUNT and per-line TEXT reach the target, which is what the picker resolution
  // determines. Callers relying on byte-exact trailing-newline equality must compare
  // per-line text, not raw exactSource.
  for (let index = lines.length - 1; index >= targetLines.length; index -= 1) {
    operations.push({ type: 'line.delete', lineId: lines[index].sourceLineId });
  }

  // Target has MORE lines: grow from the last existing line by splitting off a new
  // empty trailing line and inserting its text. Each split anchors on the line
  // created immediately before it. The split offset is always the END of the anchor
  // line's CURRENT text so the split adds a fresh empty line without disturbing the
  // anchor's content: for the first growth that is the (already edited) last
  // existing line's target text; thereafter the anchor is a freshly created empty
  // line, so the offset is 0.
  if (lines.length && targetLines.length > lines.length) {
    let anchorLineId = lines[lines.length - 1].sourceLineId;
    let anchorLength = targetLines[lines.length - 1].length; // the last kept line's final text length
    for (let index = lines.length; index < targetLines.length; index += 1) {
      const newLineId = mintLineId();
      operations.push({ type: 'line.split', lineId: anchorLineId, offset: anchorLength, newLineId, eol: '\n' });
      if (targetLines[index]) operations.push({ type: 'text.insert', lineId: newLineId, offset: 0, text: targetLines[index] });
      anchorLineId = newLineId;
      anchorLength = 0; // subsequent anchors are freshly created empty lines
    }
  }

  return operations;
}
